import { apiClient } from './api';
import type { Affiliate, ClickTracking, AffiliateStats, AffiliateCourseCommission } from '../types/affiliate';

export interface CreateAffiliateRequest {
  userId: string;
  code: string;
  commissionType: 'percentage' | 'fixed';
  commissionValue: number;
}

export interface UpdateAffiliateRequest {
  code?: string;
  commissionType?: 'percentage' | 'fixed';
  commissionValue?: number;
  isActive?: boolean;
}

export interface GenerateReferralURLRequest {
  baseUrl: string;
}

export interface TrackClickRequest {
  landingPage: string;
}

export const affiliateService = {
  // Create affiliate
  createAffiliate: async (data: CreateAffiliateRequest): Promise<Affiliate> => {
    const response = await apiClient.post<Affiliate>('/affiliates', data);
    return response.data as Affiliate;
  },

  // Get all affiliates
  getAllAffiliates: async (): Promise<Affiliate[]> => {
    const response = await apiClient.get<Affiliate[]>('/affiliates');
    return response.data as Affiliate[];
  },

  // Get affiliate by ID
  getAffiliate: async (id: string): Promise<Affiliate> => {
    const response = await apiClient.get<Affiliate>(`/affiliates/${id}`);
    return response.data as Affiliate;
  },

  // Get affiliate by code
  getAffiliateByCode: async (code: string): Promise<Affiliate> => {
    const response = await apiClient.get<Affiliate>(`/affiliates/code/${code}`);
    return response.data as Affiliate;
  },

  // Get affiliate by user ID
  getAffiliateByUserID: async (userId: string): Promise<Affiliate> => {
    const response = await apiClient.get<Affiliate>(`/affiliates/user/${userId}`);
    return response.data as Affiliate;
  },

  // Update affiliate
  updateAffiliate: async (id: string, data: UpdateAffiliateRequest): Promise<Affiliate> => {
    const response = await apiClient.put<Affiliate>(`/affiliates/${id}`, data);
    return response.data as Affiliate;
  },

  // Deactivate affiliate
  deactivateAffiliate: async (id: string): Promise<void> => {
    await apiClient.post(`/affiliates/${id}/deactivate`);
  },

  // Generate referral URL
  generateReferralURL: async (affiliateId: string, baseUrl: string): Promise<string> => {
    const response = await apiClient.post<{ referralUrl: string }>(`/affiliates/${affiliateId}/referral-url`, { baseUrl });
    return (response.data as { referralUrl: string }).referralUrl;
  },

  // Track click
  trackClick: async (code: string, landingPage: string): Promise<void> => {
    await apiClient.post(`/affiliates/track/${code}`, { landingPage });
  },

  // Get click stats
  getClickStats: async (affiliateId: string): Promise<AffiliateStats> => {
    const response = await apiClient.get<AffiliateStats>(`/affiliates/${affiliateId}/stats`);
    return response.data as AffiliateStats;
  },

  // Get my affiliate (current user) - uses affiliate ID from user context
  getMyAffiliate: async (affiliateId: string): Promise<Affiliate> => {
    const response = await apiClient.get<Affiliate>(`/affiliates/${affiliateId}`);
    return response.data as Affiliate;
  },

  // Get click tracking history for affiliate
  getAffiliateClicks: async (affiliateId: string): Promise<ClickTracking[]> => {
    const response = await apiClient.get<ClickTracking[]>(`/affiliates/${affiliateId}/clicks`);
    return response.data as ClickTracking[];
  },

  // Course Commission Management
  setCourseCommission: async (
    affiliateId: string,
    courseId: string,
    commissionType: 'percentage' | 'fixed',
    commissionValue: number
  ): Promise<AffiliateCourseCommission> => {
    const response = await apiClient.post<AffiliateCourseCommission>('/affiliates/course-commissions', {
      affiliate_id: affiliateId,
      course_id: courseId,
      commission_type: commissionType,
      commission_value: commissionValue,
    });
    return response.data as AffiliateCourseCommission;
  },

  getCourseCommission: async (affiliateId: string, courseId: string): Promise<AffiliateCourseCommission> => {
    const response = await apiClient.get<AffiliateCourseCommission>(
      `/affiliates/${affiliateId}/courses/${courseId}/commission`
    );
    return response.data as AffiliateCourseCommission;
  },

  getAffiliateCourseCommissions: async (affiliateId: string): Promise<AffiliateCourseCommission[]> => {
    const response = await apiClient.get<AffiliateCourseCommission[]>(
      `/affiliates/${affiliateId}/course-commissions`
    );
    return response.data as AffiliateCourseCommission[];
  },

  deleteCourseCommission: async (affiliateId: string, courseId: string): Promise<void> => {
    await apiClient.delete(`/affiliates/${affiliateId}/courses/${courseId}/commission`);
  },

  // Get commissions by course ID
  getCourseCommissions: async (courseId: string): Promise<AffiliateCourseCommission[]> => {
    const response = await apiClient.get<AffiliateCourseCommission[]>(`/courses/${courseId}/affiliate-commissions`);
    return response.data as AffiliateCourseCommission[];
  },
};
